import { ChangeDetectorRef, Directive, inject, NgZone, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { V2AuthConfirmationAbstract } from '@app/+login/deprecated-hybrid/components/v2-auth-confirmation.abstract.component';
import { HttpErrorResponse } from '@angular/common/http';
import { IdentifierType } from '@app/core/_models/member-interface';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SettingsService } from '@app/core/settings/settings.service';
import { SessionService } from '@app/core/session/session.service';
import storage from 'local-storage-fallback';
import { LoginService } from '@app/+login/login.service';
import { FormErrors } from '@app/shared/types/form-errors';
import { BaseParamsService } from '@app/core/base-params.service';
import { LOGIN_ID_STORAGE_ITEM_KEY } from '@app/+login/deprecated-hybrid/consts';
import { SOURCE_QUERY_PARAM } from '@app/core/consts';

const ageValidator: ValidatorFn = control => control.value === true ? null : { required: true };

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class V2SignupConfirmationAbstract extends V2AuthConfirmationAbstract implements OnInit {
  public form = new UntypedFormGroup({});

  public readonly minimumMemberAge$ = this.settingsService.minimumMemberAge$.pipe(
    tap(minimumMemberAge => {
      if (minimumMemberAge) {
        this.form.addControl('confirmAge', new UntypedFormControl(false, ageValidator), { emitEvent: false });
      }
    }),
  );

  public submitted = false;
  public formErrors: FormErrors = {};

  public hasSignUpError = false;
  public hasSignUpSuccess = false;
  public hasSignInError = false;

  public readonly identifierMsisdnType = IdentifierType.MSISDN;
  public readonly identifierEmailType = IdentifierType.EMAIL;

  private readonly baseParamsService = inject(BaseParamsService);

  protected constructor(cdr: ChangeDetectorRef,
                        router: Router,
                        route: ActivatedRoute,
                        protected sessionService: SessionService,
                        protected loginService: LoginService,
                        protected settingsService: SettingsService,
                        @Optional() private ngZone?: NgZone) {
    super(router, route, cdr);
  }

  protected prepareFormOnFormValuesChanges(): void {
    this.form.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.formErrors = {};

      if (value.consents && !!Object.keys(value.consents).length) {
        this.addOrRemovedConsentMissingMarketingIdentifierIfMissing(value.consents);
      }
      this.cdr.detectChanges();
    });
  }

  protected redirectToLogin(identifier?: string): void {
    let url = this.route.snapshot.url;
    const source = this.route.snapshot.queryParamMap.get(SOURCE_QUERY_PARAM);
    const extras: Record<string, any> = { relativeTo: this.route.parent };

    if (source) {
      const rootQSegment = this.baseParamsService.getQUrlSegment({ source }) as UrlSegment;

      if (url[0]?.path === rootQSegment?.path) {
        url = [rootQSegment, ...url.slice(1)];
      } else {
        url = [rootQSegment, ...url];
      }
    }

    if (identifier) {
      extras.state = { identifierValue: identifier };
    }
    const navigate = (): Promise<boolean> => this.router.navigate(url, extras);
    //quick fix for error after registration with recaptcha checkbox
    this.ngZone ? this.ngZone.run(() => {
      navigate();
    }) : navigate();
  }

  protected onSuccess(): void {
    this.hasSignUpError = false;
    this.hasSignUpSuccess = true;
    this.cdr.detectChanges();
  }

  protected onError(err?: HttpErrorResponse): void {
    if (err) { this.mapApiErrors(err); }
    this.submitted = true;
    this.hasSignUpError = true;
    this.cdr.detectChanges();
  }

  protected signIn(password): Observable<unknown> {
    return this.sessionService.deprecatedSignIn(this.identifier, password, this.sessionService.getOtpSecret());
  }

  protected onSignInSuccess(): void {
    storage.removeItem(LOGIN_ID_STORAGE_ITEM_KEY);
    this.loginService.afterSignedIn(this.route.parent);
  }

  protected onSignInError(): void {
    this.hasSignInError = true;
    this.cdr.detectChanges();
  }

  private mapApiErrors(err: HttpErrorResponse): void {
    let error: FormErrors = {};

    switch (err.status) {
      case 465:
        error = { password: { error: 'required_password' } };
        break;
      case 466:
        error = { password: { error: 'invalid_password' } };
        break;
      case 422:
        error = Object.keys(err.error.errors).reduce((acc, cur) => {
          const errorValue = err.error.errors[cur][0].error;
          acc[cur] = { error: errorValue === 'invalid' ? `invalid_${cur}` : errorValue };
          return acc;
        }, {});
    }
    this.formErrors = error;
  }

  private addOrRemovedConsentMissingMarketingIdentifierIfMissing(consents: any): void {
    if (this.identifier?.identifier_type == IdentifierType.EMAIL) {
      if (consents.sms_marketing && !this.form.controls.msisdn) {
        this.form.addControl('msisdn', new UntypedFormControl('', Validators.required), { emitEvent: false });
      } else if (!consents.sms_marketing && this.form.controls.msisdn) {
        this.form.removeControl('msisdn', { emitEvent: false });
      }
    } else {
      if (consents.email_marketing && !this.form.controls.email) {
        this.form.addControl('email', new UntypedFormControl('', Validators.required), { emitEvent: false });
      } else if (!consents.email_marketing && this.form.controls.email) {
        this.form.removeControl('email', { emitEvent: false });
      }
    }
  }
}
